/**
 * Age in whole years, from a `YYYY-MM-DD` date of birth.
 *
 * Computed here rather than in the API on purpose. An age is a function of the
 * birth date *and today*, so a value serialised by the backend freezes "today" at
 * the moment of the response — wrong for a ward list left open overnight, which is
 * how this screen is actually used. The birth date is the fact; the age is a view
 * of it.
 *
 * No `Date` parsing of the input: `new Date('1938-03-03')` is midnight UTC and
 * reading it back in local time can land on the previous day. Integers only, and
 * "today" is read in the viewer's local time, because that is the day the ward is
 * having.
 *
 * See docs/I18N.md §3 and the note in format/clinicalDate.ts.
 */

import { InvalidClinicalDateError } from './clinicalDate'

/**
 * Whole years completed. `today` is injectable so the tests can state a date
 * rather than depend on the day they run.
 */
export function clinicalAgeYears(birthDate: string, today: Date = new Date()): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate)
  if (match === null) {
    throw new InvalidClinicalDateError(birthDate)
  }

  const [, year, month, day] = match
  const birthYear = Number(year)
  const birthMonth = Number(month)
  const birthDay = Number(day)

  let age = today.getFullYear() - birthYear

  // Not yet had this year's birthday. Compared as (month, day) pairs rather than
  // through a subtraction, so there is no arithmetic that a leap day can shift.
  const monthNow = today.getMonth() + 1
  const dayNow = today.getDate()
  if (monthNow < birthMonth || (monthNow === birthMonth && dayNow < birthDay)) {
    age -= 1
  }

  if (age < 0) {
    // A birth date in the future is bad data, not a newborn. Throwing keeps it out
    // of a column a clinician would otherwise read as fact.
    throw new InvalidClinicalDateError(birthDate)
  }

  return age
}
